import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useThemeStore } from '../stores/themeStore'
import type { Settings as SettingsType } from '../types'
import { cn } from '../lib/utils'

function Settings() {
  const { theme, setTheme } = useThemeStore()
  const [settings, setSettings] = useState<Partial<SettingsType>>({
    polling_interval: 60,
    sound_enabled: true,
    browser_notifications_enabled: true,
    email_provider: 'none',
    email_recipient: '',
    data_retention_days: 30,
    auto_scroll: true,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    const response = await api.get<SettingsType>('/api/settings')
    if (response.success && response.data) {
      setSettings(response.data)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setMessage(null)

    const response = await api.put('/api/settings', settings)

    if (response.success) {
      setMessage({ type: 'success', text: 'Settings saved successfully!' })
    } else {
      setMessage({ type: 'error', text: response.error || 'Failed to save settings' })
    }

    setIsSaving(false)
    setTimeout(() => setMessage(null), 3000)
  }

  const updateSetting = <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        new Notification('X Monitor', {
          body: 'Browser notifications are now enabled!',
          icon: '/favicon.svg',
        })
      }
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Settings
      </h2>

      {message && (
        <div
          className={cn(
            'p-4 rounded-lg mb-6',
            message.type === 'success'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          )}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Appearance */}
        <section className="card p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Appearance
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Theme
              </label>
              <div className="flex gap-2">
                {(['light', 'dark', 'system'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={cn(
                      'px-4 py-2 rounded-lg capitalize transition-colors',
                      theme === t
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.auto_scroll}
                onChange={(e) => updateSetting('auto_scroll', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">
                Auto-scroll to new tweets
              </span>
            </label>
          </div>
        </section>

        {/* Polling */}
        <section className="card p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Data Fetching
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Polling Interval (seconds)
              </label>
              <input
                type="number"
                min="30"
                max="3600"
                value={settings.polling_interval}
                onChange={(e) => updateSetting('polling_interval', parseInt(e.target.value) || 60)}
                className="input w-32"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                How often to check for new tweets (30-3600 seconds)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data Retention (days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.data_retention_days}
                onChange={(e) => updateSetting('data_retention_days', parseInt(e.target.value) || 30)}
                className="input w-32"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                How long to keep tweets in the database (1-365 days)
              </p>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section className="card p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Notifications
          </h3>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.sound_enabled}
                onChange={(e) => updateSetting('sound_enabled', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">
                Enable sound alerts
              </span>
            </label>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.browser_notifications_enabled}
                  onChange={(e) => updateSetting('browser_notifications_enabled', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Enable browser notifications
                </span>
              </label>
              <button
                onClick={requestNotificationPermission}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Request permission
              </button>
            </div>
          </div>
        </section>

        {/* Email (Future) */}
        <section className="card p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Email Notifications
            <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
              Coming Soon
            </span>
          </h3>

          <div className="space-y-4 opacity-50 pointer-events-none">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Provider
              </label>
              <select
                value={settings.email_provider}
                onChange={(e) => updateSetting('email_provider', e.target.value as 'none' | 'resend' | 'brevo')}
                className="input"
                disabled
              >
                <option value="none">None</option>
                <option value="resend">Resend</option>
                <option value="brevo">Brevo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recipient Email
              </label>
              <input
                type="email"
                value={settings.email_recipient}
                onChange={(e) => updateSetting('email_recipient', e.target.value)}
                placeholder="your@email.com"
                className="input"
                disabled
              />
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Email notifications require API keys. Set RESEND_API_KEY or BREVO_API_KEY in your environment.
          </p>
        </section>

        {/* X API (Optional) */}
        <section className="card p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            X API Configuration
            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
              Optional
            </span>
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            The X API provides more reliable data but requires a developer account.
            Without it, we use free RSS proxies which may be less reliable.
          </p>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            To use the X API, set <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">X_BEARER_TOKEN</code> in your environment:
          </p>
          <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm overflow-x-auto">
            wrangler secret put X_BEARER_TOKEN
          </pre>
        </section>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={cn('btn-primary', isSaving && 'opacity-50 cursor-not-allowed')}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Settings
